/**
 * Environment Configuration Service
 *
 * Fetches runtime configuration from environment.json
 * This allows the frontend to work across different environments
 * without build-time dependencies.
 */

export interface EnvironmentConfig {
  apiUrl: string;
  region: string;
  environment: string;
  userPoolId: string;
  userPoolClientId: string;
  authDomain: string;
  oauthScopes: string[];
  oauthRedirectSignIn: string[];
  oauthRedirectSignOut: string[];
}

class EnvironmentService {
  private config: EnvironmentConfig | null = null;
  private configPromise: Promise<EnvironmentConfig> | null = null;

  /**
   * Fetch environment configuration from environment.json
   * This is cached after the first fetch
   */
  async getConfig(): Promise<EnvironmentConfig> {
    // Return cached config if available
    if (this.config) {
      return this.config;
    }

    // Return existing promise if already fetching
    if (this.configPromise) {
      return this.configPromise;
    }

    // Start fetching config
    this.configPromise = this.fetchConfig();
    this.config = await this.configPromise;
    return this.config;
  }

  /**
   * Get cached config synchronously (returns null if not loaded)
   */
  getCachedConfig(): EnvironmentConfig | null {
    return this.config;
  }

  /**
   * Check if config is loaded
   */
  isConfigLoaded(): boolean {
    return this.config !== null;
  }

  private async fetchConfig(): Promise<EnvironmentConfig> {
    const response = await fetch('/environment.json', {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch environment config: ${response.status} ${response.statusText}. Make sure environment.json exists in the public folder.`
      );
    }

    const config = await response.json();

    // Validate required fields
    const requiredFields = [
      'apiUrl',
      'userPoolId',
      'userPoolClientId',
      'authDomain',
    ];
    for (const field of requiredFields) {
      if (!config[field]) {
        throw new Error(`Environment config missing required field: ${field}`);
      }
    }

    return config as EnvironmentConfig;
  }
}

// Export singleton instance
export const environmentService = new EnvironmentService();
